/* MILTROO Smart Ad Engine — centralized client runtime.
   Fetches resolved ad config from /api/public/ads/config and renders
   device/page/theme-aware slots with lazy loading, frequency caps and
   dismissible containers. Never renders on checkout/cart/auth pages. */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import api from "../lib/api";

export const getAdDevice = () => {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
};

const SS_KEY = "miltroo_ads";
const sessionState = () => {
  try { return JSON.parse(sessionStorage.getItem(SS_KEY)) || { n: 0, perAd: {}, rot: 0, sid: "" }; }
  catch { return { n: 0, perAd: {}, rot: 0, sid: "" }; }
};
const saveState = (s) => { try { sessionStorage.setItem(SS_KEY, JSON.stringify(s)); } catch { /* ignore */ } };

export const adSessionId = () => {
  const s = sessionState();
  if (!s.sid) { s.sid = Math.random().toString(36).slice(2) + Date.now().toString(36); saveState(s); }
  return s.sid;
};

const canShowAd = (cfg, adId) => {
  const st = sessionState();
  if (st.n >= (cfg?.caps?.max_ads_per_session ?? 12)) return false;
  if ((st.perAd[adId] || 0) >= (cfg?.caps?.per_ad_session_cap ?? 4)) return false;
  return true;
};
const recordShown = (adId) => {
  const st = sessionState();
  st.n += 1; st.perAd[adId] = (st.perAd[adId] || 0) + 1; st.rot += 1;
  saveState(st);
};
export const rotationIndex = () => sessionState().rot;

export const trackAdEvent = (adId, event, { slug = "", page = "", placement = "", container = "", error = "" } = {}) => {
  try {
    api.post("/public/ads/event", {
      ad_id: adId, event, slug, page, placement, container, error,
      device: getAdDevice(), session_id: adSessionId(),
    }).catch(() => {});
  } catch { /* ignore */ }
};

/* ---- config cache (per slug+page+device) ---- */
const cfgCache = new Map();
export function useAdConfig(slug, page) {
  const device = getAdDevice();
  const key = `${slug}|${page}|${device}`;
  const [cfg, setCfg] = useState(() => cfgCache.get(key) || null);
  useEffect(() => {
    if (cfgCache.has(key)) { setCfg(cfgCache.get(key)); return; }
    let live = true;
    api.get("/public/ads/config", { params: { slug, page, device } })
      .then((r) => { cfgCache.set(key, r.data); if (live) setCfg(r.data); })
      .catch(() => { cfgCache.set(key, { enabled: false }); if (live) setCfg({ enabled: false }); });
    return () => { live = false; };
  }, [key]);
  return cfg;
}

const pickCandidate = (slotData) => {
  if (!slotData?.candidates?.length) return null;
  return slotData.candidates[rotationIndex() % slotData.candidates.length];
};

// per-page mount cap — enforced client-side so every page type keeps a clean layout
const pageCounts = {};

const bannerSrcDoc = (ad) => `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;overflow:hidden}</style></head><body>
${ad.full_code || `<script>atOptions = { 'key': '${ad.key}', 'format': 'iframe', 'height': ${ad.size?.h || 250}, 'width': ${ad.size?.w || 300}, 'params': {} };</script>\n<script src="${ad.script_url}"></script>`}
</body></html>`;

function BannerFrame({ ad, testid, slug = "", page = "", slot = "" }) {
  const ref = useRef(null);
  const container = `${ad.size?.w || 0}x${ad.size?.h || 0}`;
  const onLoad = () => {
    // verify the external ad script actually executed and produced content
    setTimeout(() => {
      let ok = true, err = "";
      try {
        const html = ref.current?.contentDocument?.body?.innerHTML?.trim() || "";
        if (html.length < 10) { ok = false; err = "ad script produced no visible content"; }
      } catch { ok = true; /* cross-origin — assume rendered */ }
      trackAdEvent(ad.id, ok ? "render_ok" : "render_error", { slug, page, placement: slot, container, error: err });
    }, 3000);
  };
  return (
    <iframe ref={ref} onLoad={onLoad} title={ad.name || "Advertisement"} srcDoc={bannerSrcDoc(ad)} data-testid={testid}
      width={ad.size?.w || 300} height={ad.size?.h || 250} scrolling="no" frameBorder="0"
      style={{ maxWidth: "100%", border: 0, display: "block" }} sandbox="allow-scripts allow-same-origin allow-popups" />
  );
}

function ResponsiveAd({ ad, testid, slug = "", page = "", slot = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const holder = document.createElement("div");
    holder.id = ad.container_id;
    el.appendChild(holder);
    const s = document.createElement("script");
    s.src = ad.script_url; s.async = true; s.setAttribute("data-cfasync", "false");
    el.appendChild(s);
    const t = setTimeout(() => {
      const ok = holder.childNodes.length > 0;
      trackAdEvent(ad.id, ok ? "render_ok" : "render_error", { slug, page, placement: slot, container: "responsive", error: ok ? "" : "network script produced no content" });
    }, 4000);
    return () => { clearTimeout(t); try { el.innerHTML = ""; } catch { /* ignore */ } };
  }, [ad.container_id, ad.script_url, ad.id, slug, page, slot]);
  return <div ref={ref} data-testid={testid} className="flex justify-center" />;
}

/** Central ad slot — lazy, frequency-capped, dismissible. Renders nothing when ads are off. */
export function AdSlot({ slug, page, slot, className = "", label = true }) {
  const cfg = useAdConfig(slug, page);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const wrapRef = useRef(null);
  const reserved = useRef(false);
  const capKey = `${slug}|${page}|${getAdDevice()}`;
  const ad = useMemo(() => {
    if (!cfg?.enabled) return null;
    if ((pageCounts[capKey] || 0) >= (cfg.caps?.max_ads_per_page ?? 3)) return null;
    const sd = (cfg.slots || []).find((x) => x.slot === slot);
    const picked = pickCandidate(sd);
    if (!picked || !canShowAd(cfg, picked.id)) return null;
    if (!reserved.current) { reserved.current = true; pageCounts[capKey] = (pageCounts[capKey] || 0) + 1; }
    return picked;
  }, [cfg, slot, capKey]);

  useEffect(() => {
    if (!ad || dismissed) return;
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        recordShown(ad.id);
        trackAdEvent(ad.id, "impression", { slug, page, placement: slot });
        io.disconnect();
      }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [ad, dismissed, slug, page, slot]);

  if (!cfg?.enabled || !ad || dismissed) return null;
  const size = ad.size || { w: 0, h: 0 };
  return (
    <div ref={wrapRef} className={`miltroo-ad relative flex flex-col items-center my-6 mx-auto w-fit max-w-full px-2 ${className}`}
      data-testid={`ad-slot-${slot}`} data-ad-id={ad.id}>
      {label && <p className="text-[9px] uppercase tracking-widest opacity-40 mb-1 select-none">Advertisement</p>}
      <div className="relative overflow-hidden rounded-lg" style={{ minHeight: visible && size.h ? size.h : 0, maxWidth: "100%" }}>
        {visible && (ad.ad_type === "responsive_script"
          ? <ResponsiveAd ad={ad} testid={`ad-responsive-${slot}`} slug={slug} page={page} slot={slot} />
          : <BannerFrame ad={ad} testid={`ad-banner-${slot}`} slug={slug} page={page} slot={slot} />)}
      </div>
      <button onClick={() => setDismissed(true)} data-testid={`ad-close-${slot}`} aria-label="Close advertisement"
        className="absolute -top-2 right-0 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Injects active global network scripts (e.g. social bar) — max once per session per ad.
    Never injected in SAFE experience mode (server omits them). */
export function AdNetworkScripts({ slug, page }) {
  const cfg = useAdConfig(slug, page);
  useEffect(() => {
    if (!cfg?.enabled) return;
    for (const ad of cfg.network_scripts || []) {
      const flag = `mp_net_${ad.id}`;
      if (document.querySelector(`script[data-miltroo-ad="${ad.id}"]`) || sessionStorage.getItem(flag)) continue;
      try { sessionStorage.setItem(flag, "1"); } catch { /* ignore */ }
      const s = document.createElement("script");
      s.src = ad.script_url; s.async = true; s.setAttribute("data-cfasync", "false");
      s.setAttribute("data-miltroo-ad", ad.id);
      document.body.appendChild(s);
      trackAdEvent(ad.id, "impression", { slug, page, placement: "global" });
    }
  }, [cfg, slug, page]);
  return null;
}

/** Video pre-roll overlay — sits on MILTROO's own page over the player container,
    never injected inside third-party players. Countdown → Skip → main video starts. */
export function AdPreRoll({ ad, skipDelay = 5, maxDuration = 20, slug, onDone }) {
  const [left, setLeft] = useState(skipDelay);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    trackAdEvent(ad.id, "ad_start", { slug, page: "watch", placement: "preroll" });
    const t = setInterval(() => {
      setLeft((v) => Math.max(0, v - 1));
      setElapsed((v) => v + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [ad.id, slug]);
  useEffect(() => {
    if (elapsed >= maxDuration) { trackAdEvent(ad.id, "ad_complete", { slug, page: "watch", placement: "preroll" }); onDone("complete"); }
  }, [elapsed, maxDuration, ad.id, slug, onDone]);
  const skip = () => { trackAdEvent(ad.id, "ad_skip", { slug, page: "watch", placement: "preroll" }); onDone("skip"); };
  return (
    <div className="absolute inset-0 z-20 bg-black flex flex-col items-center justify-center" data-testid="ad-preroll">
      <p className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-white/50 font-bold">Advertisement</p>
      <div className="max-w-full max-h-full overflow-hidden flex items-center justify-center">
        {ad.ad_type === "responsive_script" ? <ResponsiveAd ad={ad} testid="ad-preroll-frame" slug={slug} page="watch" slot="preroll" /> : <BannerFrame ad={ad} testid="ad-preroll-frame" slug={slug} page="watch" slot="preroll" />}
      </div>
      {left === 0 && (
        <button onClick={skip} data-testid="ad-preroll-close" aria-label="Close advertisement"
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-black/90 transition-colors">
          <X className="h-4 w-4" />
        </button>
      )}
      <div className="absolute bottom-3 right-3">
        {left > 0 ? (
          <span className="h-9 px-4 rounded-full bg-black/70 border border-white/20 text-white text-[12.5px] font-bold inline-flex items-center" data-testid="ad-preroll-countdown">
            Skip available in {left}
          </span>
        ) : (
          <button onClick={skip} data-testid="ad-preroll-skip"
            className="h-9 px-5 rounded-full bg-white text-black text-[12.5px] font-extrabold hover:bg-white/90 transition-colors">
            Skip Ad
          </button>
        )}
      </div>
    </div>
  );
}

/** Portal mount helper for store pages: returns slots config presence quickly. */
export function adsEnabledFor(cfg) {
  return !!cfg?.enabled;
}
